import React, { useEffect, useMemo, useContext } from 'react'
import { Modal, Form, Input, InputNumber, Button, Space, Select } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { GET_PRIORITY_SLA_CONFIGS } from '../graphql/Queries' // Assuming you already have this
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserSave } from 'utils/User'

const { Option } = Select

const escalateToOptions = [
  { label: 'Team Leader', value: 'TEAM_LEADER' },
  { label: 'Department Head', value: 'DEPARTMENT_HEAD' },
  { label: 'CRM Admin', value: 'CRM_ADMIN' },
]

const EscalationModal = ({ visible, onCancel, onOk, editingEscalation }) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasAdd = canUserAdd(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canSubmit = editingEscalation ? (hasEdit && hasSave) : (hasAdd && hasSave)

  const [form] = Form.useForm()
  const { data: priorityData } = useQuery(
    GET_PRIORITY_SLA_CONFIGS
  )

  const priorityOptions = useMemo(() => {
    const all = priorityData?.getPrioritySlaConfigs || []
    const uniqueMap = new Map()
    for (const p of all) {
      if (!uniqueMap.has(p.priorityLevel)) {
        uniqueMap.set(p.priorityLevel, p)
      }
    }
    return Array.from(uniqueMap.values()).map((p) => ({
      label:
        p.priorityLevel.charAt(0).toUpperCase() +
        p.priorityLevel.slice(1).toLowerCase(),
      value: p.id,
    }))
  }, [priorityData])

  useEffect(() => {
    if (editingEscalation) {
      form.setFieldsValue({
        ...editingEscalation,
        priorityId:
          editingEscalation.priorityId || editingEscalation.priority?.id,
      })
    } else {
      form.resetFields()
    }
  }, [editingEscalation, form])

  const handleSubmit = () => {
    if (!canSubmit) return
    form.validateFields().then((values) => {
      onOk(values)
    })
  }

  return (
    <Modal
      title={editingEscalation ? 'Edit Escalation Rule' : 'Add Escalation Rule'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
    >
      <Form form={form} layout='vertical'>
        <Form.Item
          label='Priority'
          name='priorityId'
          rules={[{ required: true, message: 'Please select a priority' }]}
        >
          <Select
            allowClear
            options={priorityOptions}
            placeholder='Select Priority'
            loading={!priorityData}
          />
        </Form.Item>

        <Form.Item
          label='Escalate To'
          name='escalateTo'
          rules={[
            { required: true, message: 'Please select a role to escalate to' },
          ]}
        >
          <Select placeholder='Select role'>
            {escalateToOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label='Trigger Value (in hours)'
          name='triggerValue'
          rules={[
            { required: true, message: 'Please enter trigger value in hours' },
          ]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item label='Action Description' name='actionDescription'>
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item>
          <Space
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button onClick={onCancel}>Cancel</Button>
            <Button type='primary' onClick={handleSubmit} disabled={!canSubmit}>
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default EscalationModal
